'use client';
import type { MouseEvent, ReactNode } from 'react';
import { useRouter } from 'next/navigation';

/**
 * Routes clicks on internal links inside legacy WordPress blog HTML (rendered with
 * dangerouslySetInnerHTML, so no next/link) through the App Router: client-side nav
 * keeps the visit's in-memory landing source (src/lib/useUtmParams.ts), which a full
 * page load would drop before the reader reaches a form.
 *
 * Left to the browser: modified/non-primary clicks, links with a target or download,
 * other origins, in-page #anchors, and non-page paths (/api/, files like .pdf).
 */
export function LegacyBlogContentLinks({ children }: { children: ReactNode }) {
  const router = useRouter();

  const handleClick = (event: MouseEvent<HTMLDivElement>) => {
    if (event.defaultPrevented || event.button !== 0) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    // HTML anchors only: an SVG <a> has no string href.
    const anchor = (event.target as Element).closest('a');
    if (!(anchor instanceof HTMLAnchorElement)) return;
    if (!anchor.href || anchor.target || anchor.hasAttribute('download')) return;

    const url = new URL(anchor.href);
    if (url.origin !== window.location.origin) return;
    if (url.pathname === window.location.pathname && url.hash) return;
    if (url.pathname.startsWith('/api/') || /\.[a-z0-9]+$/i.test(url.pathname)) return;

    event.preventDefault();
    router.push(`${url.pathname}${url.search}${url.hash}`);
  };

  return (
    // A delegated click handler for links the HTML string renders; the links themselves
    // stay keyboard-reachable and work without JS.
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
    <div className="contents" data-testid="legacy-blog-content" onClick={handleClick}>
      {children}
    </div>
  );
}
